// ============================================================================
// SubscribeBox — public email signup widget (Task #9).
// ============================================================================
// Drop-in card for the public homepage. Captures email (+ optional name),
// POSTs /api/subscribers, shows the generic "check your inbox" confirmation
// success state. Designed to slot under the workshop calendar.
//
// Anti-enumeration: success message is identical whether the email is new,
// already pending, or already confirmed.
// ============================================================================

import { useState } from 'react';
import { Mail, Loader2, CheckCircle2 } from 'lucide-react';
import { api } from '../api';

interface Props {
  source?: string;   // sent to the backend so admins can see where signups came from
  variant?: 'card' | 'inline';
}

export function SubscribeBox({ source = 'homepage', variant = 'card' }: Props) {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || !email.trim()) return;
    setBusy(true);
    setError('');
    try {
      await api.subscribe({
        email: email.trim(),
        full_name: fullName.trim() || null,
        source,
      });
      setDone(true);
    } catch (err: any) {
      setError(err?.message || 'Could not sign up. Try again later.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Frame variant={variant}>
        <div className="flex items-start gap-3">
          <CheckCircle2 className="h-6 w-6 text-uif-green flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-uif-green-dark">Check your inbox</div>
            <p className="text-sm text-gray-700 mt-1">
              We sent a confirmation link to <strong>{email}</strong>. Click it to start receiving notices about new workshops.
            </p>
          </div>
        </div>
      </Frame>
    );
  }

  return (
    <Frame variant={variant}>
      <form onSubmit={submit} className="space-y-3">
        <div className="flex items-center gap-2 text-uif-green-dark">
          <Mail className="h-5 w-5" />
          <div className="font-semibold">Get notified about new workshops</div>
        </div>
        <p className="text-sm text-gray-700">
          One email per new event. No spam. Unsubscribe anytime with one click.
        </p>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm">
            {error}
          </div>
        )}
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            placeholder="Your name (optional)"
            disabled={busy}
            className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
          />
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            disabled={busy}
            className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
          />
          <button
            type="submit"
            disabled={busy || !email.trim()}
            className="inline-flex items-center justify-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-4 py-2 text-sm hover:bg-uif-green-dark disabled:bg-gray-300"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            Sign me up
          </button>
        </div>
      </form>
    </Frame>
  );
}

function Frame({ variant, children }: { variant: 'card' | 'inline'; children: React.ReactNode }) {
  if (variant === 'inline') {
    return <div>{children}</div>;
  }
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
      {children}
    </div>
  );
}
